<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import MediaAssetsAPI from 'dashboard/api/mediaAssets';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  pickable: { type: Boolean, default: false },
});

const emit = defineEmits(['pick']);

const { t } = useI18n();
const router = useRouter();

const FILTERS = [
  { id: 'all', label: 'ALL' },
  { id: 'upload', label: 'UPLOADS' },
  { id: 'ai', label: 'AI' },
];

const assets = ref([]);
const usage = ref({ current_mb: 0, limit_mb: null });
const filter = ref('all');
const isUploading = ref(false);
const selected = ref(null);

const filtered = computed(() => {
  if (filter.value === 'all') return assets.value;
  return assets.value.filter(asset => asset.kind === filter.value);
});

const notifyError = error => {
  if (error?.response?.data?.code === 'storage_limit_exceeded') {
    useAlert(t('MARKETING_PAGES.FORM.STORAGE_FULL'));
    return;
  }
  useAlert(error?.response?.data?.error || t('MARKETING_PAGES.API.ERROR'));
};

const load = async () => {
  try {
    const { data } = await MediaAssetsAPI.get();
    assets.value = data.payload || [];
    usage.value = data.usage || usage.value;
  } catch (error) {
    notifyError(error);
  }
};

const uploadFiles = async files => {
  if (!files.length) return;
  isUploading.value = true;
  try {
    // Sequencial de propósito: o 402 de storage precisa interromper a fila.
    // eslint-disable-next-line no-restricted-syntax
    for (const file of files) {
      // eslint-disable-next-line no-await-in-loop
      await MediaAssetsAPI.upload(file);
    }
    useAlert(t('MARKETING_PAGES.BUILDER.MEDIA.UPLOADED'));
  } catch (error) {
    notifyError(error);
  } finally {
    isUploading.value = false;
    await load();
  }
};

const onInput = event => uploadFiles(Array.from(event.target.files || []));

const onDrop = event =>
  uploadFiles(
    Array.from(event.dataTransfer?.files || []).filter(file =>
      file.type.startsWith('image/')
    )
  );

const saveAlt = async asset => {
  try {
    const { data } = await MediaAssetsAPI.update(asset.id, {
      alt_text: asset.alt_text,
    });
    Object.assign(asset, data);
  } catch (error) {
    notifyError(error);
  }
};

const remove = async asset => {
  try {
    const { data } = await MediaAssetsAPI.usage(asset.id);
    const count = data.pages?.length || 0;
    const message = t('MARKETING_PAGES.BUILDER.MEDIA.DELETE_IN_USE', { count });
    // eslint-disable-next-line no-alert
    if (count && !window.confirm(message)) return;
    await MediaAssetsAPI.delete(asset.id);
    selected.value = null;
    await load();
  } catch (error) {
    notifyError(error);
  }
};

const toggle = asset => {
  selected.value = selected.value?.id === asset.id ? null : asset;
};

const goToBilling = () => router.push({ name: 'billing_settings_index' });

onMounted(load);
</script>

<template>
  <div class="grid content-start gap-3">
    <div class="flex items-center gap-2">
      <span class="i-lucide-image size-4 text-n-brand" />
      <h2 class="flex-1 text-sm font-semibold text-n-slate-12">
        {{ t('MARKETING_PAGES.BUILDER.MEDIA.TITLE') }}
      </h2>
      <span class="text-[11px] text-n-slate-10">{{ assets.length }}</span>
    </div>

    <label
      class="grid h-16 cursor-pointer place-items-center rounded-xl border-2 border-dashed border-n-slate-7 px-2 text-center hover:border-n-brand"
      @dragover.prevent
      @drop.prevent="onDrop"
    >
      <span class="text-xs font-semibold text-n-slate-11">
        {{
          isUploading
            ? t('MARKETING_PAGES.FORM.UPLOADING')
            : t('MARKETING_PAGES.BUILDER.MEDIA.DROP')
        }}
      </span>
      <span class="text-[10px] text-n-slate-10">
        {{ t('MARKETING_PAGES.BUILDER.MEDIA.DROP_HINT') }}
      </span>
      <input
        type="file"
        multiple
        accept="image/png,image/jpeg,image/gif,image/webp,image/svg+xml"
        class="hidden"
        :disabled="isUploading"
        @change="onInput"
      />
    </label>

    <div class="flex gap-1.5">
      <button
        v-for="item in FILTERS"
        :key="item.id"
        type="button"
        class="h-6 rounded-full px-2.5 text-[11px] font-semibold"
        :class="
          filter === item.id
            ? 'bg-n-slate-12 text-n-slate-1'
            : 'bg-n-alpha-2 text-n-slate-11'
        "
        @click="filter = item.id"
      >
        {{ t(`MARKETING_PAGES.BUILDER.MEDIA.${item.label}`) }}
      </button>
    </div>

    <p
      v-if="!filtered.length"
      class="py-6 text-center text-xs leading-5 text-n-slate-10"
    >
      {{ t('MARKETING_PAGES.BUILDER.MEDIA.EMPTY') }}
    </p>
    <div v-else class="grid grid-cols-3 gap-2">
      <button
        v-for="asset in filtered"
        :key="asset.id"
        type="button"
        class="relative h-[70px] overflow-hidden rounded-lg border-2"
        :class="
          selected?.id === asset.id ? 'border-n-brand' : 'border-transparent'
        "
        @click="toggle(asset)"
      >
        <img
          :src="asset.url"
          class="size-full object-cover"
          :alt="asset.alt_text || ''"
        />
        <span
          v-if="asset.kind === 'ai'"
          class="absolute left-1 top-1 rounded bg-n-iris-9 px-1 text-[8px] font-bold text-n-slate-1"
        >
          {{ t('MARKETING_PAGES.BUILDER.MEDIA.AI_BADGE') }}
        </span>
      </button>
    </div>

    <div v-if="selected" class="grid gap-2 rounded-xl border border-n-weak p-3">
      <input
        v-model="selected.alt_text"
        :placeholder="t('MARKETING_PAGES.BUILDER.MEDIA.ALT')"
        class="h-8 rounded-lg border border-n-weak bg-n-alpha-2 px-2 text-xs text-n-slate-12"
        @change="saveAlt(selected)"
      />
      <div class="flex flex-wrap gap-1.5">
        <Button
          v-if="pickable"
          size="sm"
          :label="t('MARKETING_PAGES.BUILDER.MEDIA.USE')"
          icon="i-lucide-check"
          @click="emit('pick', selected)"
        />
        <Button
          size="sm"
          variant="ghost"
          color="ruby"
          :label="t('MARKETING_PAGES.BUILDER.MEDIA.DELETE')"
          icon="i-lucide-trash-2"
          @click="remove(selected)"
        />
      </div>
    </div>

    <div
      class="rounded-lg bg-n-alpha-2 p-2.5 text-[11px] leading-5 text-n-slate-11"
    >
      <span v-if="usage.limit_mb">
        {{
          t('MARKETING_PAGES.BUILDER.MEDIA.USAGE', {
            used: Math.round(usage.current_mb || 0),
            limit: usage.limit_mb,
          })
        }}
      </span>
      <button
        type="button"
        class="ml-1 font-semibold text-n-brand"
        @click="goToBilling"
      >
        {{ t('MARKETING_PAGES.BUILDER.MEDIA.BUY_MORE') }}
      </button>
    </div>
  </div>
</template>
